import React, { useState, useEffect, useRef } from "react";

const TechnicalDiagram = ({ imageSrc, markers }) => {
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const [markerPositions, setMarkerPositions] = useState([]);
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const updateTimeoutRef = useRef(null);

  // Calculate marker positions based on image dimensions
  const updateMarkerPositions = () => {
    if (!imageRef.current || !containerRef.current) return;

    const img = imageRef.current;
    const rect = img.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    // Only update if dimensions have actually changed
    const positions = markers.map((marker) => ({
      ...marker,
      x: (marker.x / 100) * rect.width + (rect.left - containerRect.left),
      y: (marker.y / 100) * rect.height + (rect.top - containerRect.top),
    }));

    setMarkerPositions(positions);
  };

  // Debounced update function
  const debouncedUpdate = () => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    updateTimeoutRef.current = setTimeout(() => {
      updateMarkerPositions();
    }, 100);
  };

  useEffect(() => {
    // Initial calculation after a small delay to ensure image is loaded
    const timer = setTimeout(updateMarkerPositions, 200);

    // Recalculate on window resize with debounce
    const handleResize = () => {
      debouncedUpdate();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      clearTimeout(timer);
    };
  }, [imageSrc]); // Only depend on imageSrc

  // Update positions when markers change
  useEffect(() => {
    if (imageRef.current && imageRef.current.complete) {
      updateMarkerPositions();
    }
  }, [markers]);

  return (
    <div className="technical-image-container" ref={containerRef}>
      <img
        ref={imageRef}
        src={imageSrc}
        alt="Hardware Design"
        className="technical-diagram"
        onLoad={updateMarkerPositions}
      />
      <div className="scan-line-overlay"></div>

      {/* Interactive markers */}
      {markerPositions.map((marker, index) => (
        <div
          key={`marker-${index}-${marker.x}-${marker.y}`}
          className="marker-container"
          style={{
            position: "absolute",
            left: `${marker.x}px`,
            top: `${marker.y}px`,
            transform: "translate(-50%, -50%)",
          }}
          onMouseEnter={() => setHoveredMarker(index)}
          onMouseLeave={() => setHoveredMarker(null)}
        >
          {/* Marker pin */}
          <div className="marker-pin">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="3" />
              <circle cx="12" cy="12" r="10" />
            </svg>
          </div>

          {/* Tooltip */}
          {hoveredMarker === index && (
            <div className="marker-tooltip">
              <h4>{marker.title}</h4>
              <p>{marker.description}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default TechnicalDiagram;

